import type { ICreateUserCmd } from "@/data/commands/user/CreateUserCmd";
import type { ISetOperatorSettingsCmd } from "@/data/commands/user/SetOperatorSettingsCmd";
import type { ISetUserPasswordCmd } from "@/data/commands/user/SetUserPasswordCmd";
import type { IUpdateUserGrantsCmd, IUserGrantAccessCmd } from "@/data/commands/user/UserGrantAccessCmd";
import type { IReportFilterEntityBase } from "@/data/model/ReportFilterEntityBase";
import type { ILinkableEntity } from "@/data/model/Shared";
import type { IDevice, IIdentity, ILoginProvider, IOperatorSettings, IRole, IUser, IUserAccessGrant } from "@/data/model/User";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import type { IUsersQry } from "@/data/queries/user/UsersQry";
import { DateTime } from "@/data/util/DateTime";



import { BaseServiceDao, type IDataSource, type IEditableDataSource, type IMultiEntityLinkDataSource, type IPaginatedResult, parseCollection, parseMultiLanguageText, parsePaginatedResult } from "./BaseServiceDao";
import { LocationDao } from "./LocationDao";
import { RoomDao } from "./RoomDao";


export class UserDao extends BaseServiceDao implements IEditableDataSource<IIdentity>, IMultiEntityLinkDataSource {
    public static className = "UserDao";

    constructor() {
        super("user");
    }

    public async getSingle(id: string): Promise<IIdentity> {
        return this.get(id, undefined, UserDao.parseIdentity);
    }

    public async getData(qry: IUsersQry): Promise<IPaginatedResult<IIdentity>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, UserDao.parseIdentity));
    }

    public async create(cmd: ICreateUserCmd): Promise<IIdentity> {
        return this.post("", cmd, UserDao.parseIdentity, true);
    }

    public async update(id: string, cmd: ICreateUserCmd): Promise<IIdentity> {
        return this.put(id, cmd, UserDao.parseIdentity, true);
    }

    public async setPassword(id: string, cmd: ISetUserPasswordCmd): Promise<IIdentity> {
        return this.post(id + "/password", cmd);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, UserDao.parseUser);
    }

    public async getRoles(qry: IPaginatedQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IRole>> {
        return this.get("role", qry, (f) => parsePaginatedResult(f, UserDao.parseRole), abortSignal);
    }

    public userRoleDataSource: IDataSource<IRole> = {
        getData: (filter, abortSignal) => this.getRoles(filter, abortSignal),
    };

    public async getOperatorSettings(id: string): Promise<IOperatorSettings | undefined> {
        return this.get(id + "/operator-settings/", undefined, UserDao.parseOperatorSettings);
    }

    public async setOperatorSettings(
        id: string,
        data: ISetOperatorSettingsCmd
    ): Promise<IOperatorSettings | undefined> {
        return this.put(`${id}/operator-settings/`, data, UserDao.parseOperatorSettings);
    }

    public async updateUserGrants(id: string, data: IUpdateUserGrantsCmd): Promise<IPaginatedResult<IUserAccessGrant>> {
        return this.put(id + "/grant", data, (f) => parsePaginatedResult(f, UserDao.parseUserAccessGrant));
    }

    public async getGrants(
        userId: string,
        qry: IPaginatedQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IUserAccessGrant>> {
        return this.get(
            userId + "/grant",
            qry,
            (f) => parsePaginatedResult(f, UserDao.parseUserAccessGrant),
            abortSignal
        );
    }

    //acl

    public async getAccessLocationGrants(
        userId: string,
        qry: IPaginatedQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IUserAccessGrant>> {
        return this.get(
            userId + "/acl/",
            qry,
            (f) => parsePaginatedResult(f, UserDao.parseUserAccessGrant),
            abortSignal
        );
    }

    public async grantAccess(userId: string, cmd: IUserGrantAccessCmd): Promise<IUserAccessGrant> {
        return this.post(userId + "/acl/", cmd, UserDao.parseUserAccessGrant);
    }

    public async removeAccess(userId: string, claimId: number): Promise<void> {
        return this.delete(userId + "/acl/" + claimId);
    }

    public async search(excludeIds: Array<string>, term?: string): Promise<Array<ILinkableEntity>> {
        return this.get("search", { Exclude: excludeIds, Term: term }, (f) =>
            parseCollection(f, (h) => {
                return {
                    id: h.id,
                    name: h.name,
                };
            })
        );
    }

    public aclDataSource(user: string): IDataSource<IUserAccessGrant> {
        return {
            getData: (filter, abortSignal) => {
                const grants = this.getAccessLocationGrants(user, filter, abortSignal);
                return grants;
            },
        };
    }

    public static parseReportFilterEntityBase(literal: any): IReportFilterEntityBase {
        return {
            id: literal.id,
            name: literal.name,
        };
    }

    public static parseIdentity(literal: any): IIdentity {
        return {
            id: literal.id,
            firstName: literal.firstName == null ? undefined : literal.firstName,
            lastName: literal.lastName == null ? undefined : literal.lastName,
            avatarPath: literal.avatarPath != null ? literal.avatarPath : undefined,
            identityCardPath: literal.identityCardPath != null ? literal.identityCardPath : undefined,
            phoneNumber: literal.phoneNumber != null ? literal.phoneNumber : undefined,
            email: literal.email != null ? literal.email : undefined,
            zip: literal.zip != null ? literal.zip : undefined,
            birthday: literal.birthday != null ? DateTime.parseIsoDate(literal.birthday) : undefined,
            lastLogin: literal.lastLogin != null ? DateTime.parseIsoDate(literal.lastLogin) : undefined,
            created: DateTime.parseIsoDate(literal.created),
            devices: parseCollection(literal.devices, UserDao.parseDevice),
            language: parseInt(literal.language),
            user: literal.user != null ? UserDao.parseUser(literal.user) : undefined,
            walleeExternalCustomerId:
                literal.walleeExternalCustomerId == null ? undefined : literal.walleeExternalCustomerId,
        };
    }

    public static parseUser(literal: any): IUser {
        return {
            email: literal.email,
            emailConfirmed: literal.emailConfirmed,
            roles: parseCollection(literal.roles, (c) => c),
            loginProviders: parseCollection(literal.loginProviders, UserDao.parseLoginProvider),
        };
    }

    public static parseRole(literal: any): IRole {
        return {
            id: literal.id,
            name: literal.name,
        };
    }

    public static parseDevice(literal: any): IDevice {
        return {
            id: literal.id,
            created: DateTime.parseIsoDate(literal.created),
            deviceFamily: parseInt(literal.deviceFamily),
            fingerprint: literal.fingerprint,
            ipAddress: literal.ipAddress,
        };
    }

    public static parseLoginProvider(literal: any): ILoginProvider {
        return {
            loginProvider: literal.loginProvider,
            providerKey: literal.providerKey,
        };
    }

    public static parseUserAccessGrant(literal: any): IUserAccessGrant {
        return {
            claimId: parseInt(literal.claimId),
            claimType: literal.claimType,
            claimValue: literal.claimValue,
            userId: literal.userId,
            userName: literal.userName,
            location: literal.location != null ? literal.location : undefined,
        };
    }

    public static parseOperatorSettings(literal: any): IOperatorSettings | undefined {
        if (literal == null) return undefined;
        return {
            operatorPlaySoundModesList: parseCollection(literal.operatorPlaySoundModesList, (c) => parseInt(c)),
            fulfillmentGroups: parseCollection(literal.fulfillmentGroups, (d) => {
                return {
                    id: d.id,
                    name: parseMultiLanguageText(d.name),
                    internalName: d.internalName,
                };
            }),
            location: literal.location == null ? undefined : LocationDao.parseLocation(literal.location),
            planRoom: literal.planRoom == null ? undefined : RoomDao.parseRoomWithService(literal.planRoom),
            orderRooms: parseCollection(literal.orderRooms, (d) => {
                return {
                    id: d.id,
                    name: d.name,
                };
            }),
            productionTileViewMode: literal.productionTileViewMode,
            newOrdersViewMode: literal.newOrdersViewMode,
            fulfilmentTileSize: literal.fulfilmentTileSize,
            orderStateMode: literal.orderStateMode,
        };
    }
}
